/**
 * Institution Recommendations Panel
 * Shows recommended financial institutions based on investment horizon.
 * Used by Goal Calculator and Growth Calculator.
 */
import React from 'react';
import { Landmark, Briefcase, Shield, TrendingUp, Building2, Clock } from 'lucide-react';
import './InstitutionRecommendations.css';

const INSTITUTIONS = {
  banco: {
    icon: Landmark,
    name: 'Bancos',
    desc: 'Liquidez total, rendimientos bajos. Ideal cuando necesitas el dinero "ya".'
  },
  sofipo: {
    icon: Building2,
    name: 'Sofipos',
    desc: 'Reguladas por la CNBV. Tasas mejores que bancos, con seguro de depósito (Prosofipo).'
  },
  casaBolsa: {
    icon: Briefcase,
    name: 'Casas de Bolsa',
    desc: 'Acceso a CETES, bonos y acciones. Mejor rendimiento, mayor diversificación.'
  },
  aseguradora: {
    icon: Shield,
    name: 'Aseguradoras',
    desc: 'Planes de ahorro e inversión a largo plazo con beneficios fiscales y protección.'
  },
  afore: {
    icon: TrendingUp,
    name: 'Administradoras de Activos',
    desc: 'Fondos de inversión diversificados con gestión profesional. Pensados para crecer.'
  }
};

const HORIZON_CONFIG = {
  short: {
    label: 'Corto Plazo',
    range: '1 mes – 2 años',
    color: '#FFB800',
    emoji: '⚡',
    description: 'Para metas próximas. Prioridad: liquidez y bajo riesgo.',
    options: ['banco', 'sofipo']
  },
  medium: {
    label: 'Mediano Plazo',
    range: '+2 a 5 años',
    color: '#39FF14',
    emoji: '🎯',
    description: 'Balance entre rendimiento y disponibilidad. Diversifica.',
    options: ['sofipo', 'casaBolsa', 'afore']
  },
  long: {
    label: 'Largo Plazo',
    range: '+5 años',
    color: '#00D9FF',
    emoji: '🚀',
    description: 'Aprovecha el interés compuesto al máximo. Más rendimiento, menos liquidez.',
    options: ['casaBolsa', 'aseguradora', 'afore']
  }
};

const getHorizon = (years) => {
  if (years <= 2) return 'short';
  if (years <= 5) return 'medium';
  return 'long';
};

const InstitutionRecommendations = ({ years, onCtaClick, source = 'default' }) => {
  if (!years || years <= 0) return null;

  const horizonKey = getHorizon(years);
  const horizon = HORIZON_CONFIG[horizonKey];

  return (
    <div className="recommendations-panel" data-testid={`recommendations-${source}`}>
      <div className="recommendations-header">
        <div className="horizon-badge" style={{ borderColor: horizon.color, color: horizon.color }}>
          <Clock className="w-4 h-4" />
          <span>{horizon.emoji} {horizon.label}</span>
          <span className="horizon-range">· {horizon.range}</span>
        </div>
        <h4 className="recommendations-title">¿Dónde poner tu dinero?</h4>
        <p className="recommendations-subtitle">{horizon.description}</p>
      </div>

      <div className="institutions-grid">
        {horizon.options.map((key) => {
          const inst = INSTITUTIONS[key];
          const Icon = inst.icon;
          return (
            <div key={key} className="institution-card" style={{ '--accent-color': horizon.color }}>
              <Icon className="institution-icon" style={{ color: horizon.color }} />
              <h5 className="institution-name">{inst.name}</h5>
              <p className="institution-desc">{inst.desc}</p>
            </div>
          );
        })}
      </div>

      {onCtaClick && (
        <div className="recommendations-cta">
          <p className="recommendations-cta-text">
            ¿No sabes cuál elegir? Un asesor te ayuda a definir el mix correcto para tu meta. 👇
          </p>
          <button
            type="button"
            onClick={onCtaClick}
            className="recommendations-cta-button"
            data-testid="recommendations-cta-button"
          >
            Quiero asesoría gratuita
          </button>
        </div>
      )}

      <p className="recommendations-disclaimer">
        * Estas son categorías generales reguladas por CNBV, CNSF y Condusef. Tu asesor te orientará sobre productos específicos.
      </p>
    </div>
  );
};

export default InstitutionRecommendations;
